<template>
    <teleport to="#side-layer-overlay-teleport">
        <!-- Dark Layer Transition -->
        <transition
        name="overlay-modal-animation"
        appear
        @after-enter="showModalWrapper = true"
        @after-leave="emit('hideMe')">
            <div v-if="showMe" class="overlay-modal-wrapper">
                <transition name="up-below-animation" appear @after-leave="showMe = false">
                    <div  v-if="showModalWrapper" class="modal-container" v-click-outside="() => { showModalWrapper = false }">
                        <!-- Title -->
                        <span>{{defaultTitle}}</span>
                        <!-- Buttons -->
                        <div class="buttons-container" :class="{'invert-button-order': invertButtonOrder}">
                            <button @click="emit('openGallery')">
                                <icon-image/>
                                {{defaultGalleryButton}}
                            </button>
                            <button @click="emit('openCamera')">
                                <icon-camera/>
                                {{defaultPhotoButton}}
                            </button>
                        </div>
                    </div>
                </transition>
            </div>
        </transition>
    </teleport>
</template>

<script setup>
import IconImage from '@/assets/icons/image-search.svg?inline'
import IconCamera from '@/assets/icons/add-photo.svg?inline'

/* Vue */
import { ref, watch, computed } from 'vue'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideMe', 'openCamera', 'openGallery'])

/* Props
@title {String} - Gallery Title
@importButtonLabel {String} - Import Button Label
@cameraButtonLabel {String} - Camera Button Label
@invertButtonOrder {Boolean} - prop to invert buttons order
@triggerClose {Boolean} - prop to trigger close of filter modal so animation happens before our modal disapears
*/
const props = defineProps({
    title: String,
    importButtonLabel: String,
    cameraButtonLabel: String,
    invertButtonOrder: Boolean,
    triggerClose: Boolean
})

const showMe = ref(true)
const showModalWrapper = ref(false)

watch(() => props.triggerClose, () => { showModalWrapper.value = false })

const defaultTitle = computed(() => {
    return props.title ? props.title : t('uploader.options')
})

const defaultGalleryButton = computed(() => {
    return props.importButtonLabel ? props.importButtonLabel : t('uploader.importGallery')
})

const defaultPhotoButton = computed(() => {
    return props.cameraButtonLabel ? props.cameraButtonLabel : t('uploader.takePicture')
})
</script>

<style lang="scss" scoped>
.overlay-modal-wrapper {
    @include modalsCenteredOverlayTeleportStyles;
    align-items: flex-end;

    z-index: 370;
    .modal-container {
        @include upBelowEntryAnimation;

        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 16px;

        flex-shrink: 0;
        box-sizing: border-box;
        border-radius: 16px 16px 0 0;
        background-color: var(--secondary-light-4);

        span {
            color: var(--secondary-dark-3);
            font-size: 16px;
            font-weight: 500;
            line-height: 20px;
        }

        .buttons-container {
            width: 100%;
            margin-top: 16px;

            display: flex;
            justify-content: center;
            align-items: flex-start;
            flex-direction: column;

            &.invert-button-order {
                flex-direction: column-reverse;
            }

            button {
                min-height: 40px;
                gap: 8px;

                display: flex;
                justify-content: flex-start;
                align-items: center;

                @include H9-Regular;
                font-size: 13px;
                color: var(--secondary-dark-3);

                cursor: pointer;

                svg {
                    width: 16px;
                    height: 16px;

                    :deep(.change-color-fill) {
                        fill: var(--secondary-dark-2);
                    }
                }
            }
        }
    }
}
</style>
